import {
  ISpecificationCategoryResponse,
  ISpecificationItemResponse
} from "@/shared/services/interface/responses/ISpecificationResponse";

// Helper to get subcategories from children array
const getSubCategories = (
  category: ISpecificationCategoryResponse
): ISpecificationCategoryResponse[] => {
  if (!category.children || !Array.isArray(category.children)) {
    return [];
  }
  return category.children.filter(
    (child) => child.type === "category"
  ) as ISpecificationCategoryResponse[];
};

// Helper to get items from children array
const getItems = (
  category: ISpecificationCategoryResponse
): ISpecificationItemResponse[] => {
  if (!category.children || !Array.isArray(category.children)) {
    return [];
  }
  return category.children.filter(
    (child) => child.type === "item"
  ) as ISpecificationItemResponse[];
};

export const getAllCategoryNames = (
  categories: ISpecificationCategoryResponse[]
): string[] => {
  let names: string[] = [];

  categories.forEach((category) => {
    // Add main category name
    names.push(category.name);

    // Add subcategories if they exist
    const subCategories = getSubCategories(category);
    if (subCategories.length > 0) {
      names = [...names, ...getAllCategoryNames(subCategories)];
    }
  });

  return names;
};

export const getCategoryItems = (
  category: ISpecificationCategoryResponse
) => {
  return getItems(category);
};

export const getAllCategoryIds = (
  categories: ISpecificationCategoryResponse[]
): string[] => {
  let ids: string[] = [];
  categories.forEach((category) => {
    ids.push(category.id);
    const subCategories = getSubCategories(category);
    if (subCategories.length > 0) {
      ids = [...ids, ...getAllCategoryIds(subCategories)];
    }
  });
  return ids;
};

interface ItemPriorityResult {
  priority: string;
  categoryPath: string[];
}

export const getItemsPriority = (
  categories: ISpecificationCategoryResponse[],
  item: ISpecificationItemResponse
): ItemPriorityResult => {
  const findItemInCategory = (
    category: ISpecificationCategoryResponse,
    mainCategoryIndex: number,
    subCategoryIndex?: number,
    parentCategories: string[] = []
  ): ItemPriorityResult | null => {
    const currentPath = [...parentCategories, category.id];

    const items = getItems(category);
    const itemIndex = items.findIndex((i) => i.id === item.id);
    if (itemIndex !== -1 && itemIndex !== undefined) {
      return {
        priority:
          subCategoryIndex !== undefined
            ? `${mainCategoryIndex + 1}.${subCategoryIndex + 1}.${
                itemIndex + 1
              }`
            : `${mainCategoryIndex + 1}.${itemIndex + 1}`,
        categoryPath: currentPath
      };
    }

    const subCategories = getSubCategories(category);
    for (let i = 0; i < subCategories.length; i++) {
      const result = findItemInCategory(
        subCategories[i],
        mainCategoryIndex,
        subCategoryIndex !== undefined ? subCategoryIndex : i,
        currentPath
      );
      if (result) return result;
    }

    return null;
  };

  for (let i = 0; i < categories.length; i++) {
    const result = findItemInCategory(categories[i], i);
    if (result) return result;
  }

  return {
    priority: "",
    categoryPath: []
  };
};
